<script setup lang="ts">
import type { PropertySearchCriteria } from '@@/shared/property/search-criteria';
import type { LocationApiSearchResult } from '@@/shared/location/types';

defineProps<{
  open: boolean
  mainLocation?: LocationApiSearchResult
}>();

const emit = defineEmits<{
  'update:open': [boolean]
  'alert': [PropertySearchCriteria]
}>();

function openFilters() {
  emit('update:open', true);
}

const route = useRoute();
const { propertySearchCriteria } = usePropertySearchCriteria(route);
const { filtersCount, secondaryFiltersCount } = usePropertySearchCriteriaCount(propertySearchCriteria);

// https://css-tricks.com/how-to-detect-when-a-sticky-element-gets-pinned/
// NOTE: top position is at -1px to trigger the intersection observer
const propertyFilterBar = ref<HTMLDivElement>();
const isStuck = ref(false);
useIntersectionObserver(propertyFilterBar, (entries) => {
  const entry = entries[0];
  if (!entry) return isStuck.value = false;
  isStuck.value = entry.intersectionRatio < 1;
}, { threshold: [1] });

function onOpenAlerts(alertPropertySearchCriteria: PropertySearchCriteria) {
  emit('alert', alertPropertySearchCriteria);
}
</script>

<template>
  <IadWrapper
    ref="propertyFilterBar"
    tag="div"
    class="sticky top-[-1px] bg-white z-10"
    :class="{
      'transition-shadow': true,
      'shadow-[0_4px_3px_0_rgba(0,0,0,0.2)]': isStuck,
      'pt-s lg:pb-xs': !isStuck,
      'py-s': isStuck,
    }"
  >
    <div class="hidden lg:flex lg:gap-l">
      <PropertyFilterType @alert="onOpenAlerts" />
      <PropertyFilterLocation
        :main-location
        @alert="onOpenAlerts"
      />
      <PropertyFilterSurface @alert="onOpenAlerts" />
      <PropertyFilterPrice @alert="onOpenAlerts" />
      <PropertyFilterRoom @alert="onOpenAlerts" />
      <IadBtn
        variant="outlined"
        class="hidden lg:flex"
        @click="openFilters"
      >
        <Icon name="iad-icon:tune" />
        {{ $t(`property_serp.filters.other`, { count: secondaryFiltersCount, secondaryFiltersCount: $n(secondaryFiltersCount) }) }}
      </IadBtn>
      <IadBtn
        variant="filled"
        @click="onOpenAlerts(propertySearchCriteria)"
      >
        <Icon name="iad-icon:notifications" />
        {{ $t(`property_serp.notification.create_alert`) }}
      </IadBtn>
    </div>
    <div class="gap-xl lg:hidden grid grid-cols-2 items-center">
      <IadBtn
        variant="outlined"
        class="lg:hidden"
        @click="openFilters"
      >
        <Icon name="iad-icon:tune" />
        {{ $t('core.common.filter', { count: filtersCount, filtersCount: $n(filtersCount) }) }}
      </IadBtn>
      <PropertySort />
    </div>
  </IadWrapper>
</template>
